export interface Locations {
  list: Location[]
  associations: string[]
}

export interface Location {
  default: boolean
  email?: string
  phoneNumber?: string
  representativePerson?: Representative
  address: Address
  googlePlace?: GooglePlace
}

export interface GooglePlace {
  rating?: number
  userRatingCount?: number
}

export interface Representative {
  firstName: string
  lastName: string
  role?: string
}

export interface Address {
  streetAndHouseNumber?: string
  locality: string
  postalCode: number
}

/**
 * MapLoader is responsible for creating the Map URLs for the locations and also "preloading" three map locations
 * (previous, current, next) into an array which improves rendering on template.
 */
export class MapLoader {
  index = 0
  private readonly locations: MapLocation[]
  private readonly preparedLocations: MapLocation[] = new Array(3)

  constructor(locations: Locations, gMapsApiKey: string) {
    this.locations = locations.list.map(l => new MapLocation(l, locations.associations, gMapsApiKey))
    this.preparedLocations[0] = this.locations[0]
    this.preparedLocations[0].show()

    if (this.hasNext) {
      this.preparedLocations[1] = this.locations[1]
    }
  }

  get current(): MapLocation {
    return this.locations[this.index]
  }

  get hasNext(): boolean {
    return this.index + 1 < this.locations.length
  }

  get hasPrevious(): boolean {
    return this.index > 0
  }

  get preloadedLocations(): MapLocation[] {
    return this.preparedLocations.filter(l => !!l)
  }

  next(): void {
    if (!this.hasNext) return
    this.index++
    this.preloadLocations((index: number): number => (index === 2 ? 0 : index + 1), 1)
  }

  previous(): void {
    if (!this.hasPrevious) return
    this.index--
    this.preloadLocations((index: number): number => (index === 0 ? 2 : index - 1), -1)
  }

  /**
   * Prepares the next/previous maps so the template always has the previous, current and next map preloaded. It cycles
   * through the array, when reaching the end, starts at 0 again, and if we're going backwards, when reaching 0, starts
   * at 2 again.
   * @param nextIndex function to determine the next index i.e. if we're moving forward and index = 2, the next index is 0
   * @param direction the direction we're moving in i.e. forward = 1, backwards = -1
   */
  preloadLocations(nextIndex: (index: number) => number, direction: 1 | -1): void {
    for (let i = 0; i <= 2; i++) {
      if (this.preparedLocations[i].showMap && this.preparedLocations[nextIndex(i)]) {
        this.preparedLocations[i].hide()
        this.preparedLocations[nextIndex(i)].show()

        if (this.hasNext) {
          this.preparedLocations[nextIndex(nextIndex(i))] = this.locations[this.index + 1 * direction]
        }

        break
      }
    }
  }
}

export class MapLocation {
  readonly url: string
  showMap = false

  constructor(
    readonly location: Location,
    readonly associations: string[],
    gMapsApiKey: string,
  ) {
    this.url = getMapUrl(location.address, 10, gMapsApiKey)
  }

  show(): void {
    this.showMap = true
  }

  hide(): void {
    this.showMap = false
  }
}

export function getMapUrl(address: Address, zoom: number, gMapsApiKey: string): string {
  const {streetAndHouseNumber, postalCode, locality} = address

  const getMapAddress = () => {
    const mapAddress: (string | number)[] = []
    if (streetAndHouseNumber) {
      mapAddress.push(streetAndHouseNumber)
    }
    mapAddress.push(postalCode)
    mapAddress.push(locality)
    mapAddress.push('Switzerland')
    return mapAddress.join('%20')
  }

  return `https://www.google.com/maps/embed/v1/place?key=${gMapsApiKey}&q=${getMapAddress()}&zoom=${zoom}`
}
